import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DEFAULT_KIOSK_TIMEOUT_MS,
  fetchKioskTimeoutMs,
} from "../api/kioskAdmin";
import { clearKioskSession } from "../utils/kioskSession";
import IdleScreenTimeModal from "./modals/IdleScreenTimeModal";

const ACTIVITY_DEBOUNCE_MS = 1000;
const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "mousedown",
  "mousemove",
  "keydown",
  "touchstart",
  "click",
  "scroll",
];

/** Idle modal runs on every screen except welcome, sign-in and scan. */
const shouldTrackIdle = (pathname = "") => {
  const path = (pathname || "/").replace(/\/+$/, "") || "/";
  return path !== "/" && path !== "/login" && path !== "/scan";
};

/**
 * After the admin-configured idle time with no input, ask “Are you still there?”.
 * No answer for the same period again → clear session and go back to /.
 */
function KioskSessionManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const [timeoutMs, setTimeoutMs] = useState(DEFAULT_KIOSK_TIMEOUT_MS);
  const [loaded, setLoaded] = useState(false);
  const [showIdleModal, setShowIdleModal] = useState(false);

  const warningTimerRef = useRef(null);
  const expireTimerRef = useRef(null);
  const debounceRef = useRef(null);
  const timeoutMsRef = useRef(timeoutMs);
  const loadedRef = useRef(loaded);
  const pathnameRef = useRef(location.pathname);
  const showModalRef = useRef(false);

  timeoutMsRef.current = timeoutMs;
  loadedRef.current = loaded;
  pathnameRef.current = location.pathname;
  showModalRef.current = showIdleModal;

  useEffect(() => {
    let cancelled = false;
    fetchKioskTimeoutMs().then((next) => {
      if (cancelled) return;
      setTimeoutMs(next > 0 ? next : DEFAULT_KIOSK_TIMEOUT_MS);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const clearTimers = useCallback(() => {
    [warningTimerRef, expireTimerRef, debounceRef].forEach((ref) => {
      if (ref.current) {
        window.clearTimeout(ref.current);
        ref.current = null;
      }
    });
  }, []);

  const goHome = useCallback(() => {
    setShowIdleModal(false);
    clearKioskSession();
    navigate("/", { replace: true, state: { sessionResetAt: Date.now() } });
  }, [navigate]);

  const goHomeRef = useRef(goHome);
  goHomeRef.current = goHome;

  const showWarning = useCallback(() => {
    setShowIdleModal(true);
    if (expireTimerRef.current) window.clearTimeout(expireTimerRef.current);
    expireTimerRef.current = window.setTimeout(() => {
      goHomeRef.current();
    }, timeoutMsRef.current);
  }, []);

  const scheduleWarning = useCallback(() => {
    if (warningTimerRef.current) window.clearTimeout(warningTimerRef.current);
    warningTimerRef.current = window.setTimeout(
      showWarning,
      timeoutMsRef.current
    );
  }, [showWarning]);

  const resetTimer = useCallback(() => {
    clearTimers();
    setShowIdleModal(false);
    if (!loadedRef.current || !shouldTrackIdle(pathnameRef.current)) return;
    scheduleWarning();
  }, [clearTimers, scheduleWarning]);

  useEffect(() => {
    if (!loaded) return;
    if (!shouldTrackIdle(location.pathname)) {
      clearTimers();
      setShowIdleModal(false);
      return;
    }
    if (!showModalRef.current) {
      clearTimers();
      scheduleWarning();
    }
  }, [clearTimers, loaded, location.pathname, scheduleWarning, timeoutMs]);

  useEffect(() => {
    if (!loaded || !shouldTrackIdle(location.pathname)) return undefined;

    const onActivity = () => {
      if (showModalRef.current) return;
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(resetTimer, ACTIVITY_DEBOUNCE_MS);
    };

    const options = { passive: true, capture: true };
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, onActivity, options)
    );
    return () => {
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, onActivity, options)
      );
    };
  }, [loaded, location.pathname, resetTimer]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  return (
    <IdleScreenTimeModal
      show={showIdleModal}
      onContinue={resetTimer}
      onQuit={() => {
        clearTimers();
        goHome();
      }}
    />
  );
}

export default KioskSessionManager;
